import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Input,
  Table,
  Spin,
  Switch,
  Popconfirm,
  message,
  Tooltip,
} from "antd";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

import { SubscriptionItem } from "./model";
import { ColumnsType } from "antd/lib/table";
import {
  changeStatus,
  delSubscription,
  duplicate,
  getSubscriptionList,
  testSubscription,
} from "./api";
import cronstrue from "cronstrue/i18n";
import usePagination from "../../hooks/usePagination";
import auth from "../../Service/AuthFetchService";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  SettingOutlined,
} from "@ant-design/icons";

const IndexSubscriptionView: React.FC = () => {
  auth.appendLog(`天机-数据订阅页面`);
  const [search, setSearch] = useState({
    onlyMine: false,
    searchKey: "",
  });
  const [pagination, setPagination] = usePagination({
    total: 0,
    pageNo: 1,
    pageSize: 20,
  });
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const [refreshCount, setRefreshCount] = useState(0);

  const columns: ColumnsType<SubscriptionItem> = [
    {
      title: "订阅任务名称",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "创建人",
      dataIndex: "creatorName",
      key: "creatorName",
      width: 100,
    },
    {
      title: "发送时间",
      dataIndex: "cronExp",
      key: "cronExp",
      render: (value) => {
        return (
          <>{value ? cronstrue.toString(value, { locale: "zh_CN" }) : "-"}</>
        );
      },
    },
    {
      title: "上次发送时间",
      dataIndex: "lastSendTime",
      key: "lastSendTime",
      width: 250,
      render: (value) => {
        return <>{dayjs(value).format("YYYY-MM-DD HH:mm:ss")}</>;
      },
    },
    {
      title: "订阅状态",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (value, record) => {
        return (
          <>
            <Switch
              checked={record.status === 1 ? true : false}
              onChange={(e) => switchStatus(record.id, e)}
              checkedChildren="已订阅"
              unCheckedChildren="未订阅"
            />
          </>
        );
      },
    },
    {
      title: "上次修改时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 250,
      render: (value, record) => {
        return (
          <>
            {dayjs(value).format("YYYY-MM-DD HH:mm:ss")}
            {record.updaterName && <span>({record.updaterName})</span>}
          </>
        );
      },
    },
    {
      title: "操作",
      dataIndex: "actions",
      key: "actions",
      width: 240,
      render: (text, record) => {
        return (
          <Row>
            <Col>
              <Tooltip title={"编辑"}>
                <Button
                  icon={<EditOutlined />}
                  type="link"
                  onClick={() => editSubscription(record.id)}
                ></Button>
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title={"复制"}>
                <Button
                  icon={<CopyOutlined />}
                  type="link"
                  onClick={() => duplicateSubscription(record.id)}
                ></Button>
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title={"测试发送"}>
                <Button
                  icon={<SettingOutlined />}
                  type="link"
                  onClick={() => testSend(record.id)}
                ></Button>
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title={"删除"}>
                <Popconfirm
                  onConfirm={() => confirmDelete(record.id)}
                  title="确定要删除订阅吗？"
                  okText="确定"
                  cancelText="取消"
                >
                  <Button icon={<DeleteOutlined />} type="link" danger></Button>
                </Popconfirm>
              </Tooltip>
            </Col>
          </Row>
        );
      },
    },
  ];

  const editSubscription = (id: number) => {
    history.push(`subscription/edit/${id}`);
  };

  // 改变订阅状态
  const switchStatus = (id: number, status: boolean) => {
    changeStatus({ id, status })
      .then(() => {
        message.success("修改成功");
      })
      .catch((e) => {
        message.error("操作失败" + e.toString());
      })
      .finally(() => {
        afterEdit();
      });
  };

  // 删除订阅
  const confirmDelete = (id: number) => {
    delSubscription(id)
      .then(() => {
        message.success("删除成功");
      })
      .catch((e) => {
        message.error("操作失败" + e.toString());
      })
      .finally(() => {
        afterEdit();
      });
  };

  const duplicateSubscription = (id: number) => {
    duplicate(id)
      .then(() => {
        message.success("复制成功");
      })
      .catch((e) => {
        message.error("操作失败" + e.toString());
      })
      .finally(() => {
        afterEdit();
      });
  };

  const testSend = (id: number) => {
    testSubscription(id)
      .then(() => {
        message.success("发送成功");
      })
      .catch((e) => {
        message.error("发送失败", e.toString());
      });
  };

  const [rows, setRows] = useState<SubscriptionItem[]>([]);

  useEffect(() => {
    // 查询列表
    const getList = async () => {
      setLoading(true);
      const res = await getSubscriptionList(search, {
        pageNo: pagination.pageNo,
        pageSize: pagination.pageSize,
      });
      setRows(res.items);
      setPagination.setTotal(res.total);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    };
    getList();
  }, [pagination.pageNo, pagination.pageSize, search, refreshCount]);

  const createNewBook = () => {
    history.push("subscription/create");
  };

  const [scrollY, setScrollY] = useState(document.body.clientHeight - 285);
  // 监听窗口大小变化
  useEffect(() => {
    const onResize = () => {
      setScrollY(document.body.clientHeight - 285);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const afterEdit = () => {
    setRefreshCount(refreshCount + 1);
  };

  return (
    <>
      <Row style={{ marginTop: "12px" }}>
        <Col>
          <Input.Search
            value={search.searchKey}
            onChange={({ target: { value } }) => {
              setPagination.setPageNo(1);
              setSearch({ ...search, searchKey: value });
            }}
            placeholder="按名字、创建者搜索"
            style={{ width: "100%" }}
          />
        </Col>
        <Col flex="auto"></Col>
        <Col>
          <Button type="primary" onClick={createNewBook}>
            新增订阅
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: "12px" }}>
        <Col flex="100%">
          <Table
            columns={columns}
            dataSource={rows}
            size="small"
            rowKey="id"
            pagination={{
              total: pagination.total,
              pageSize: pagination.pageSize,
              current: pagination.pageNo,
              onChange: (page, pageSize) => {
                setPagination.setPageNo(page);
                setPagination.setPageSize(pageSize);
              },
            }}
            scroll={{
              y: scrollY,
              x: 1000,
            }}
            bordered
            loading={{
              spinning: loading,
              indicator: <Spin />,
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default IndexSubscriptionView;
